import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { lazy, memo, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import BackLink from '../back-link/app';

import useAttributeChange from '@apps/match-at-glance/hooks';
import { useMatchStream } from '@hooks/useMatchStream';
import { useWindowSize } from '@hooks/useWindowSize';
import AudioButton from '@shared/AudioButton';
import { useAudioService } from '@shared/AudioButton/useAudioService';
import { useEventbus } from '@shared/hooks/useEventbus';
import type { MatchHeaderProps } from '@shared/MatchHeaderWrap';
import MatchHeaderWrap from '@shared/MatchHeaderWrap';
import MatchStream from '@shared/MatchStream';
import MatchJsonLd from '@shared/MatchUnit/MatchJsonLd';
import PromoAppNotification from '@shared/PromoAppNotification';
import type { PkTheme } from '@shared-types/common.types';
import type { Match } from '@shared-types/match.types';
import { storyTellerApiKey } from '@utilities/WindowContext';

const StoryWidget = lazy(() => import('@shared/StoryWidget'));

import * as classes from './styles.module.scss';

type MatchHeaderOptions = MatchHeaderProps['options'] & {
  match: Match;
  theme?: PkTheme;
  bottomPanelSelector?: string;
  showAudiocommentary?: boolean;
  showNotificationIcon?: boolean;
  blockHighlights?: boolean;
  notificationOpeningTime?: number;
  streamMessage?: boolean;
};

const MatchHeader = ({ options }: { options: MatchHeaderOptions }) => {
  const {
    match,
    showNotificationIcon,
    bottomPanelSelector,
    showAudiocommentary,
    theme,
    blockHighlights,
    notificationOpeningTime,
    streamMessage,
  } = options;
  const variant = useAttributeChange('.js-match-content', 'data-ab-test-variant');

  const [refreshKey, setRefreshKey] = useState(0);
  const [hasError, setHasError] = useState(false);
  const { isDesktop, isLandscape } = useWindowSize();
  const { stream, highlightsVideo } = useMatchStream(match, true);
  const { showButton } = useAudioService(showAudiocommentary);
  const onScroll = useWindowScroll();

  const hasVideo = !!stream || !!highlightsVideo;

  const streamElRef = useRef<HTMLDivElement>(null);
  const [streamHeight, setStreamHeight] = useState<number>(0);

  const muRef = useRef<HTMLDivElement>(null);

  const [muAffix, setMuAffix] = useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isFloatingStreamEnabled, setIsFloatingStreamEnabled] = useState(true);

  useEventbus(() => {
    setRefreshKey((prevKey) => prevKey + 1);
  }, 'ab-refresh');

  useEffect(() => {
    if (!notificationOpeningTime) return;

    const timeout = setTimeout(() => {
      setPopoverOpen(true);
    }, notificationOpeningTime);

    return () => {
      clearTimeout(timeout);
    };
  }, [notificationOpeningTime]);

  const handleStoryError = () => {
    setHasError(true);
  };

  onScroll(
    useThrottledCallback(
      () => {
        if (!muRef.current) return;
        const { bottom } = muRef.current.getBoundingClientRect();

        const streamH = streamElRef.current?.clientHeight ?? 0;
        setStreamHeight(streamH);
        const isAffix = isDesktop || isLandscape ? bottom < 0 : bottom < streamH;
        setMuAffix(isAffix);
        setPopoverOpen(false);
      },
      [isDesktop, isLandscape],
      100,
    ),
  );

  const recalcHeader = () => {
    setIsFloatingStreamEnabled(false);
  };

  const header = useMemo(
    () => (
      <div className={clsx(classes.header, 'pk-px--s pk-pt--xs', hasVideo && 'pk-pb--xs pk-pb-sm--s')}>
        <BackLink appearance='outlined' className={classes.backLink} highContrast={false} />
        {variant === 'A' && !muAffix && !hasError && !!storyTellerApiKey && (
          <Suspense>
            <StoryWidget
              key={refreshKey}
              categories={[match.id]}
              className={classes.storyWidget}
              onError={handleStoryError}
              showPopover={popoverOpen}
              disablePopover={!notificationOpeningTime}
              seasonYear={match.seasonYear}
            />
          </Suspense>
        )}
        {showNotificationIcon && (
          <PromoAppNotification
            bottomPanelSelector={bottomPanelSelector}
            disablePromoPopover
            disableButtonText
            promoCssClass={clsx('pk-ml--s', classes.promoButton)}
          />
        )}
        {showButton && <AudioButton onlyIcon className={clsx('pk-ml--s', classes.audioButton)} />}
      </div>
    ),
    [
      hasVideo,
      variant,
      muAffix,
      hasError,
      refreshKey,
      match.id,
      match.seasonYear,
      popoverOpen,
      notificationOpeningTime,
      showNotificationIcon,
      bottomPanelSelector,
      showButton,
    ],
  );
  const affixHeader = useMemo(
    () => (
      <div
        className={clsx(
          classes.matchUnitAffix,
          'match-unit--affix__sticky pk-border-bottom pk-theme--light',
          showButton && classes.matchUnitAffixWithAudio,
          !isFloatingStreamEnabled && classes.matchUnitAffixDismissVideo,
        )}
      >
        {!hasError && !!storyTellerApiKey && (
          <Suspense>
            <StoryWidget
              key={refreshKey}
              categories={[match.id]}
              className={classes.storyWidgetAffix}
              disablePopover
              onError={handleStoryError}
              seasonYear={match.seasonYear}
            />
          </Suspense>
        )}
        {showButton && <AudioButton className={classes.audioButton} />}
        <MatchHeaderWrap match={match} className={classes.matchUnit} isCompactMode options={options} />
      </div>
    ),
    [showButton, isFloatingStreamEnabled, hasError, refreshKey, match, options],
  );

  return (
    <div
      key={refreshKey}
      className={clsx(classes.container, !!hasVideo && classes.containerWithStream)}
      pk-theme={theme}
      style={
        {
          '--stream-height': `${streamHeight}px`,
        } as CSSProperties
      }
    >
      {!isDesktop && !isLandscape && header}
      {hasVideo && (
        <div ref={streamElRef} className={classes.matchStream}>
          <MatchStream
            match={match}
            wrapperClassName='pk-m--0'
            blockHighlights={blockHighlights}
            streamMessage={streamMessage}
            recalcHeader={recalcHeader}
          />
        </div>
      )}
      <div ref={muRef} className={classes.content}>
        {(isDesktop || isLandscape) && header}
        <MatchJsonLd match={match} />
        <MatchHeaderWrap match={match} options={options} />
      </div>

      {muAffix && affixHeader}
    </div>
  );
};

export default memo(MatchHeader);
